import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Provider } from '@angular/core';
import { filter, finalize, Observable, ReplaySubject, tap } from 'rxjs';

import { ERROR_CODES } from '@common/utils';

import { AuthenticationResourceController } from '@portal/shared/data-access';
import { IsNotNull, JwtResponseDto, REAUTH_MANAGER, ReauthManager } from '@portal/shared/utils';

export function provideReauthManager(): Provider {
    return [
        {
            provide: REAUTH_MANAGER,
            deps: [AuthenticationResourceController],
            useFactory: (
                controller: AuthenticationResourceController,
                pendingReauthSource: ReplaySubject<JwtResponseDto> | null = null
            ): ReauthManager => ({
                isRefreshRequest: (req: HttpRequest<unknown>): boolean => req.url.includes('refresh-token'),

                isRefreshTokenInvalid: (req: HttpRequest<unknown>, err: HttpErrorResponse) =>
                    req.url.includes('refresh-token') && err.status === ERROR_CODES.UNAUTHORIZED,

                pendingReauth: (): Observable<JwtResponseDto> | null =>
                    IsNotNull(pendingReauthSource) ? pendingReauthSource.asObservable() : null,

                refreshToken: (refreshToken: string) => {
                    if (pendingReauthSource) {
                        return pendingReauthSource.pipe(filter(IsNotNull));
                    } else {
                        pendingReauthSource = new ReplaySubject<JwtResponseDto>(1);

                        return controller.refreshToken(refreshToken).pipe(
                            tap((jwtResponse: JwtResponseDto) => {
                                pendingReauthSource?.next(jwtResponse);
                            }),
                            finalize(() => {
                                pendingReauthSource?.complete();
                                pendingReauthSource = null;
                            })
                        );
                    }
                },

                logout: () => {
                    pendingReauthSource?.complete();
                    pendingReauthSource = null;

                    return controller.logout();
                },
            }),
        },
    ];
}
